'use client';

import { useEffect, useState } from 'react';
import { useUser } from '@/features/auth/model/auth.selectors';
import { onboardingService } from '../api/onboarding';
import { OnboardingModal } from './OnboardingModal';

/**
 * Auto-shows the onboarding carousel exactly once per account, driven by the
 * server-computed `should_show_onboarding` lifetime latch on the current user.
 * The flag is true ONLY while the carousel has never been auto-shown in the
 * account's lifetime AND the account is bare. The moment we auto-open we POST
 * /user/onboarding-shown (best-effort, see `markOnboardingShown`), which flips
 * the latch to false forever — so the slides never auto-show again on later
 * logins, even on a fresh device (no local "dismissed" persistence; same model
 * on Apple/Android, all three read the same /api/v1/user attribute).
 *
 * Manual replays live in the Help section (OnboardingModal mounted directly):
 * those ignore this flag and never call the mark endpoint, so re-watching the
 * intro never re-arms or re-consumes the auto-show latch.
 *
 * Within a session, closing the modal hides it for that session via the local
 * `dismissed` state so it doesn't bounce back; the latch already prevents any
 * future auto-show across sessions.
 */
export function OnboardingAutoStart() {
  const user = useUser();
  const [open, setOpen] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  // Open exactly once per session, when the server latch is explicitly true.
  // Equality check (not `!== false`) avoids opening before /api/v1/user
  // populates the field on the user payload. On open we mark it shown so the
  // server latch closes for every future session/device.
  useEffect(() => {
    if (dismissed) return;
    if (user?.should_show_onboarding === true) {
      setOpen(true);
      void onboardingService.markOnboardingShown();
    }
  }, [user?.should_show_onboarding, dismissed]);

  return (
    <OnboardingModal
      open={open}
      onOpenChange={(value) => {
        setOpen(value);
        if (!value) setDismissed(true);
      }}
      onComplete={() => setDismissed(true)}
    />
  );
}
